import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
} from '@material-ui/core';
import { CloudUpload as CloudUploadIcon, Close as CloseIcon } from '@material-ui/icons';
import { useDropzone } from 'react-dropzone';
import Snackbar from '../../components/Snackbar';
import Pagination from '../../components/Pagination';

import useStyles from './styles';
import { SUPER_ADMIN } from '../../utils/const/user';

import { uploadProductImages } from '../../requests/api/product';

function ProductsImages() {
  const classes = useStyles();
  const { t } = useTranslation();
  const account = useSelector((state) => state.account);

  const [files, setFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  // snackbar
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');

  if (account.user.role !== SUPER_ADMIN) {
    window.location.href = '/home';
  }

  const handleUpload = async () => {
    const rawFiles = files.map((f) => f.raw);
    const response = await uploadProductImages(rawFiles);

    if (!response.success) {
      setMessage(`Error subiendo imágenes: ${response.data.errorMessage}`);
      setSeverity('error');
      return;
    }

    setMessage('Las imágenes fueron cargadas');
    setSeverity('success');
    setFiles([]);
  };

  const resizeImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 300;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 300, 300);
        canvas.toBlob((blob) => {
          const resizedFile = new File([blob], file.name, { type: file.type });
          resolve({ resizedFile, width: img.width, height: img.height });
        }, file.type);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    multiple: true,
    onDrop: async (acceptedFiles) => {
      const resizedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          try {
            const { resizedFile, width, height } = await resizeImage(file);
            return {
              name: file.name,
              size: resizedFile.size,
              type: file.type,
              width,
              height,
              raw: resizedFile,
            };
          } catch (error) {
            return null;
          }
        }),
      );
      setFiles((prev) => [...prev, ...resizedFiles.filter(Boolean)]);
    },
  });

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((file) => file.name !== name));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  //  Paginación
  const indexOfFirstItem = currentPage * itemsPerPage;
  const indexOfLastItem = indexOfFirstItem + itemsPerPage;
  const currentFiles = files.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className={classes.content}>
      <Typography className={classes.title} variant="h3">
        {t('products.uploadImages')}
      </Typography>
      <Typography className={classes.subtitle} variant="subtitle1">
        {t('products.subtitle')}
      </Typography>

      {files.length === 0 && (
        <Paper {...getRootProps()} className={classes.dropZone} elevation={isDragActive ? 6 : 2}>
          <input {...getInputProps()} />
          <CloudUploadIcon className={classes.cloud} fontSize="large" color="action" />
          <Typography variant="body1" color="textSecondary">
            {isDragActive ? t('products.dropHere') : t('products.dragAndDrop')}
          </Typography>
        </Paper>
      )}

      {files.length > 0 && (
        <>
          <div className={classes.buttons}>
              <Button
                className={classes.botonCancelar}
                variant="outlined"
                onClick={() => setFiles([])}>
                  Cancelar
              </Button>
              <Button
                className={classes.botonCargar}
                variant="contained"
                onClick={handleUpload}>
                  Cargar
              </Button>
          </div>
          <Paper className={classes.tableWrapper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('products.productImages.fileName')}</TableCell>
                  <TableCell>{t('products.productImages.fileSize')}</TableCell>
                  <TableCell>{t('products.productImages.fileFormat')}</TableCell>
                  <TableCell>{t('products.productImages.fileDimensions')}</TableCell>
                  <TableCell align="right">{t('products.productImages.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentFiles.map((file) => (
                  <TableRow key={file.name}>
                    <TableCell>{file.name}</TableCell>
                    <TableCell>{formatSize(file.size)}</TableCell>
                    <TableCell>{file.type}</TableCell>
                    <TableCell>{file.width} x {file.height}px</TableCell>
                    <TableCell align="right">
                      <IconButton className = {classes.deleteIcon} size="small" onClick={() => removeFile(file.name)}>
                        <CloseIcon fontSize="medium" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              page={currentPage}
              quantity={files.length}
              rowsPerPage={itemsPerPage}
              setPage={setCurrentPage}
            />
          </Paper>
        </>
      )}
      <div>
          <Snackbar
            open={message !== ''}
            message={message}
            severity={severity}
            onClose={() => setMessage('')}
          />
      </div>
    </div>
  );
}

export default ProductsImages;
